import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { formatMmAsCm } from '~/utils/units';
import type { ReformatSourceGeometry } from '~/hooks/image/useReformatParameters';

interface SourceGeometryProps {
    geometry: ReformatSourceGeometry;
    /** Identity and acquisition details, for a series not yet loaded. */
    seriesInfo: {
        seriesUid: string;
        forUid: string;
        physicalSize: string;
        scanDateTime: string;
    } | null;
}

/**
 * What the reformat is reading from.
 *
 * Placed under {@link OutputGeometry}, in space that column already had,
 * rather than above the images where it cost a full row of dialog height.
 * What identifies the series — its modality, its slice count and its
 * matrix — is in the dialog's title instead, so only what the title has
 * no room for is left here.
 *
 * The identifiers are blurred until the viewer's sensitive-info toggle is
 * on, as everywhere else they are shown.
 */
export default function SourceGeometry({
    geometry,
    seriesInfo,
}: SourceGeometryProps) {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id });

    const spacing = geometry.isUniform
        ? t('reformatUniformSpacing')
        : t('reformatNonUniformSpacing');

    // Flowed rather than tabulated: these are reference values read once,
    // not numbers compared line by line the way the output geometry's are,
    // and a column of nine rows was pushing the panel past the height of a
    // laptop screen.
    // Modality, slice count and matrix are in the dialog's title, which
    // is a line the panel was paying for anyway.
    const facts: [string, string][] = [
        [
            t('reformatVoxelSize'),
            `${formatMmAsCm(geometry.pixelSpacingMm[0], 3)} × ${formatMmAsCm(
                geometry.pixelSpacingMm[1],
                3
            )} × ${formatMmAsCm(geometry.sliceSpacingMm, 3)} cm`,
        ],
        [
            t('reformatSpacing'),
            geometry.isTilted
                ? `${spacing}, ${intl.formatMessage(
                      { id: 'reformatOblique' },
                      { angle: geometry.tiltDeg.toFixed(1) }
                  )}`
                : spacing,
        ],
    ];

    if (seriesInfo) {
        facts.push(
            [t('reformatPhysicalSize'), seriesInfo.physicalSize],
            [t('reformatScan'), seriesInfo.scanDateTime]
        );
    }

    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
            >
                {t('reformatSourceInfo')}
            </Typography>
            <Typography
                variant="caption"
                display="block"
                sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.5 }}
            >
                {facts.map(([label, value], index) => (
                    <Box
                        component="span"
                        key={label}
                        // A pair is one unit: the line breaks BETWEEN
                        // pairs, never inside a value, so a figure is
                        // never split from its unit.
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {index > 0 && (
                            <Box
                                component="span"
                                sx={{ color: 'text.secondary' }}
                            >
                                {', '}
                            </Box>
                        )}
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                            {label}{' '}
                        </Box>
                        {value}
                    </Box>
                ))}
            </Typography>
            {seriesInfo && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ wordBreak: 'break-all', lineHeight: 1.5 }}
                >
                    {t('reformatSeriesUidLabel')}{' '}
                    <SensitiveInfoTypography>
                        {seriesInfo.seriesUid}
                    </SensitiveInfoTypography>
                    {', '}
                    {t('reformatForUidLabel')}{' '}
                    <SensitiveInfoTypography>
                        {seriesInfo.forUid}
                    </SensitiveInfoTypography>
                </Typography>
            )}
        </Box>
    );
}
